import React, {useEffect, useState} from "react";
import clsx from "clsx";
import PropTypes from "prop-types"; 


import {Typography, Container, TextField, MenuItem, Grid} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import {useStyles} from "./TicketDetails.styles";
import useCancellationToken from "../../../../hooks/useCancellationToken";
import { useEnqueueSnackbar } from "../../../../hooks/useEnqueueSnackbar";
import ValetInventoryService from "../../../../services/ValetInventoryService";
import ValetParkingService from "../../../../services/ValetParkingService";
import { PARKING_ACTIVITY_STATUS } from "../../../../constants";
import { useForm, Controller } from "react-hook-form";
import { useCoreEntityContext } from "../../../../hooks/useCoreEntitySlice";
import { useSelector } from "react-redux";

import apiClient from "../../../../auth/apiClient";

const TicketDetails = ({credentialId, isAutoRefresh}) => {
  const classes = useStyles();
  const enqueueSnackbar = useEnqueueSnackbar();
  const valetinventoryService = new ValetInventoryService(apiClient);
  const valetParkingService = new ValetParkingService(apiClient);
  const [loading, setLoading] = useState(false);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [retrievedByEmployees, setRetrievedByEmployees] = useState([]);
  const useCoreEntitySlice = useCoreEntityContext();
  const entityID = useSelector((state) =>
    useCoreEntitySlice
      ? state.coreEntities.ContextID
      : state.entities?.ContextID
  );
  const unAssignedRetrivalEmployee = "00000000-0000-0000-0000-000000000000";
  const ticketNumber = ticketDetails?.ticketNumber;
  const retrievedBy = ticketDetails?.retrievedBy ? ticketDetails?.retrievedBy : unAssignedRetrivalEmployee ;
  const [selectedRetrievedBy, setSelectedRetrievedBy] = useState(unAssignedRetrivalEmployee);
  const Label = ({ title, classes }) => {
  return (
    <Typography color="primary" className={clsx(classes.centered, classes.labelTitle)}>
      {title}
    </Typography>
  );
};

    useEffect(() => {
    if (credentialId) {
      executeDetailsQuery();
      setSelectedRetrievedBy(retrievedBy);
    }
  }, [credentialId,retrievedBy]);

  useEffect(() => {
    if (entityID && isAutoRefresh) {
      fetchEmployees();
    }
  }, [entityID, isAutoRefresh]);

  const fetchEmployees = async () => {
    try {
      let result = await valetParkingService.getValetEmployees(entityID);   
      let valetEmployees = (result.data || []).map((emp) => ({
        ...emp,
        accessHolderName: emp.lastName
          ? `${emp.firstName} ${emp.lastName}`
          : emp.firstName,
      }));
      valetEmployees = [
        {
          accessHolderId: unAssignedRetrivalEmployee,
          accessHolderName: "unassigned",
        },
        ...valetEmployees,
      ];
      setRetrievedByEmployees(valetEmployees);
    } catch (error) {
      enqueueSnackbar("Failed to get Retrieved By Employees", {
        variant: "error",
        tag: "FailedToLoadDetails",
      });
    }
  };

  const getDetails = async ({cancelToken}) => {
    setLoading(true);
		const res = await valetinventoryService.getValetTicketDetails(credentialId, cancelToken);
    setLoading(false);
    setTicketDetails(res.data);
  }

  const {execute: executeDetailsQuery} = useCancellationToken({
    func: getDetails,
    errHandleFunc: () => {
      setLoading(false);
      enqueueSnackbar("Failed to load details", {
        variant: "error",
        tag: "FailedToLoadDetails",
      });
    },
  });

  const handleChange = async (event) => {
    try {
      const {
        target: { value },
      } = event;
      let retrievalRequest = {
        entityID,
        ticketNumber,
        retrievedBy: value === unAssignedRetrivalEmployee ? null : value,
      };
      //if status is requested, mark the ticket as Retrieved with the selected Retrieval employee
      if(ticketDetails?.statusID == PARKING_ACTIVITY_STATUS.VALET_REQUEST){
        await valetParkingService.submitRetrieval(retrievalRequest);
        enqueueSnackbar("Ticket successfully retrieved", "success");
        setSelectedRetrievedBy(value);
      }else if(ticketDetails?.statusID == PARKING_ACTIVITY_STATUS.VALET_RETRIEVAL){
        //if status is 'Retrived' should update the selected Retrieval employee
        await valetParkingService.updateRetrievedByEmployee(retrievalRequest);
        enqueueSnackbar(" Ticket successfully reassigned", "success"); 
        setSelectedRetrievedBy(value);     
      }
    } catch (error) {
      enqueueSnackbar("Error while saving or updating retrieval employee", {
        variant: "error",
        tag: "FailedToLoadDetails",
      });
    }
  };

  const {
    control,
  } = useForm({
    defaultValues: {
      retrievedByEmployee: ticketDetails?.retrievedBy
        ? ticketDetails?.retrievedBy
        : unAssignedRetrivalEmployee,
    },
    mode: "all",
  });

  const getStatusLabel = (statusID) => {
    switch (statusID) {
      case PARKING_ACTIVITY_STATUS.VALET_ARRIVAL:
        return "Arrived";
      case PARKING_ACTIVITY_STATUS.VALET_REQUEST:
        return "Requested";
      case PARKING_ACTIVITY_STATUS.VALET_RETRIEVAL:
        return "Retrieved";
      case PARKING_ACTIVITY_STATUS.VALET_DELIVERY:
        return "Delivered";
      default:
        return "";
    }
  };

  const getSelectedRetrievedBy = (selectedRetrievedBy) => {
    const retrievedByEmp = retrievedByEmployees.some(retrievedBy => retrievedBy.accessHolderId === selectedRetrievedBy)
      ? selectedRetrievedBy : unAssignedRetrivalEmployee
    return retrievedByEmp;
  }

  return (
    <>
      {loading ? (
        <Container>
          <Skeleton
            className={classes.skeleton}
            variant="rect"
            height="2rem"
            animation="wave"
          />
          <Skeleton
            className={classes.skeleton}
            variant="rect"
            height="2rem"
            animation="wave"
          />
          <Skeleton
            className={classes.skeleton}
            variant="rect"
            height="2rem"
            animation="wave"
          />
        </Container>
      ) : (
        <>
          {ticketDetails !== null && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={8} sm={8}>
                  <Label title="Status" classes={classes}></Label>

                  <Typography
                    variant="h6"
                    className={clsx(classes.centered, classes.fadeInStatus)}
                    data-value={"status"}
                  >
                    {getStatusLabel(ticketDetails?.statusID)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Label title="Ticket" classes={classes}></Label>
                  <Typography>{ticketDetails?.ticketNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Label title="Plate" classes={classes}></Label>
                  <Typography>{ticketDetails?.licensePlate}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Label title="Color" classes={classes}></Label>
                  <Typography>{ticketDetails?.color}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Label title="Make" classes={classes}></Label>
                  <Typography>{ticketDetails?.make}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Label title="Model" classes={classes}></Label>
                  <Typography>{ticketDetails?.model}</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Label title="Guest Type" classes={classes}></Label>
                  <Typography>{ticketDetails?.guestType}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Label title="Rate" classes={classes}></Label>
                  <Typography>{ticketDetails?.rate}</Typography>
                </Grid>
                {isAutoRefresh && (
                  <Grid item xs={12} sm={8}>
                    <Label title="Retrieval Employee" classes={classes}></Label>
                    <Controller
                      name="retrievedByEmployee"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id="retrievedByEmployee"
                          style={{ width: "300px" }}
                          select
                          variant="outlined"
                          margin="normal"
                          data-testid="retrievedByEmployee-select"
                          onChange={(e) => {
                            field.onChange(e);
                            handleChange(e);
                          }}
                          value={getSelectedRetrievedBy(selectedRetrievedBy)}
                          disabled={
                            ticketDetails?.statusID ==
                            PARKING_ACTIVITY_STATUS.VALET_DELIVERY
                          }
                        >
                          {retrievedByEmployees.map((emp) => (
                            <MenuItem
                              key={emp.accessHolderId}
                              value={emp.accessHolderId}
                            >
                              {emp.accessHolderName}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );
};

TicketDetails.defaultProps = {
  credentialId: 0
};
TicketDetails.propTypes = {
  credentialId: PropTypes.number
};

export default TicketDetails;
